/**
 * /packages — onboarding package list route.
 *
 * Gated behind VITE_ENABLE_PACKAGE_HOME feature flag. When the flag is off,
 * the component returns null (the route still exists but renders nothing).
 * Redirect to / is handled in app.tsx route guard.
 *
 * IA position: top-level surface — packages are the business container above
 * ingest batches. See PRD phase-04 for IA decisions.
 */

import { useNavigate, Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Package, Plus } from 'lucide-react'
import { cn } from '../lib/utils'
import { useAppStore } from '../stores/app-store'
import { PackageList } from '../features/packages/package-list'
import { CreatePackageModal } from '../features/packages/create-package-modal'

const PACKAGE_HOME_ENABLED = import.meta.env.VITE_ENABLE_PACKAGE_HOME === 'true'

export function PackagesPage() {
  const { t } = useTranslation('packages')
  const navigate = useNavigate()
  const { theme } = useAppStore()
  const isDark = theme === 'dark'

  // M4 fix: redirect to / instead of blank shell — matches packages.$id.tsx pattern
  if (!PACKAGE_HOME_ENABLED) return <Navigate to="/" replace />

  const handleCreateClick = () => {
    // P1 stub: navigate to /packages with ?create=1 for future modal wiring
    navigate('/packages?create=1')
  }

  return (
    <div className={cn('flex h-full flex-col', isDark ? 'bg-surface-0' : 'bg-neutral-50')}>
      {/* Page header */}
      <div
        className={cn(
          'flex items-center gap-3 border-b px-6 py-4',
          isDark ? 'border-white/[0.06] bg-surface-1' : 'border-neutral-200 bg-white',
        )}
      >
        <Package
          className={cn('h-5 w-5', isDark ? 'text-brand-400' : 'text-brand-600')}
          aria-hidden="true"
        />
        <div className="flex-1">
          <h1
            className={cn('text-base font-semibold', isDark ? 'text-neutral-100' : 'text-neutral-900')}
          >
            {t('title')}
          </h1>
          <p className={cn('text-xs', isDark ? 'text-neutral-400' : 'text-neutral-500')}>
            {t('subtitle')}
          </p>
        </div>
        <button
          type="button"
          onClick={handleCreateClick}
          className="flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-500"
        >
          <Plus className="h-3.5 w-3.5" aria-hidden="true" />
          {t('list.create.button')}
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-6">
        <PackageList onCreateClick={handleCreateClick} />
      </div>

      {/* Create-package modal — opens when ?create=1 is in the URL */}
      <CreatePackageModal />
    </div>
  )
}
